/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Dot,
  LeftArrow,
  OrangeCheckCircle,
  SurveyCheckGreen,
} from '@jobseeker/lotus/assets/icons';
import {Image} from '@jobseeker/lotus/components/Image';
import ButtonV2 from '@jobseeker/lotus/elements/ButtonV2';
import {
  useAlertCallback,
  useIsLargeDevice,
  useWrappedTheme,
} from '@jobseeker/lotus/hooks';
import {Box, Grid, Link, Typography, styled} from '@mui/material';
import {useEffect, useMemo, useRef, useState} from 'react';
import ReviewView from './internalComponents/review';
import ScreeningView from './internalComponents/screeningView';

import type {AppState} from '@jobseeker/common-core/types';
import {shallowEqual, useSelector} from 'react-redux';

import jobDetailService from '@jobseeker/common-core/services/jobdetail.service';
import {AccordionGroup, SidePopup} from '@jobseeker/lotus/components';
import CircleLoader from '@jobseeker/lotus/components/Loader';
import {VersionVariants} from '@jobseeker/lotus/enums';
import {getOldLink} from '@jobseeker/lotus/utils';
import kebabCase from 'lodash/kebabCase';
import dynamic from 'next/dynamic';
import {useRouter} from 'next/router';
import Loader from './internalComponents/Loader';

import mediaService from '@jobseeker/common-core/services/media.service';
import {Tooltip} from '@jobseeker/lotus/elements';
import {IoIosInformationCircleOutline} from 'react-icons/io';
import {TrackEventUBA, showErrorToast} from '@jobseeker/common-core/utils';

const PLATFORM = process.env.NEXT_PUBLIC_APP_NAME;
const isAppHirist = PLATFORM === 'hirist';
const isAppIIMJobs = PLATFORM === 'iimjobs';

const parseResumeUpdatedAt = (value?: string) => {
  if (!value) return null;
  // Expected format: "YYYY-MM-DD HH:mm:ss"
  const parsed = Date.parse(value.replace(' ', 'T'));
  return Number.isNaN(parsed) ? null : new Date(parsed);
};

const Button = ({tooltipTitle, ...props}: any) => {
  if (tooltipTitle) {
    return (
      <Tooltip title={tooltipTitle} arrow>
        <Box>
          <ButtonV2 {...props} />
        </Box>
      </Tooltip>
    );
  } else {
    return <ButtonV2 {...props} />;
  }
};

const sendSwitchEvent = (data: any) => {
  const switchApplyFlowEvent = new CustomEvent('stepSwitchEvent', {
    detail: data,
    bubbles: true,
    cancelable: true,
    composed: false,
  });

  window.dispatchEvent(switchApplyFlowEvent);
};

const InfoPopup = () => {
  const [show, setShow] = useState(false);
  const [right, setRight] = useState<number | undefined>();
  const isLarge = useIsLargeDevice();
  useEffect(() => {
    const handleInfoPopupPosition = () => {
      const mainElement = document
        ?.getElementsByClassName('applyFlowContainer')[0]
        .getBoundingClientRect();
      // setRight(isLarge ? mainElement.x : mainElement.x / 2);
      setRight(isLarge ? mainElement.x : 8);
      // setRight(mainElement.x);
    };
    handleInfoPopupPosition();
    window.addEventListener('resize', handleInfoPopupPosition);
    return () => window.removeEventListener('resize', handleInfoPopupPosition);
  }, []);
  if (!right) {
    return '';
  }
  return (
    <SidePopup
      show={show}
      variant={VersionVariants.VERSION_TWO}
      onClose={() => setShow(false)}
      onToggle={() => setShow(x => !x)}
      heading="Need help with script?"
      subtitle={
        <Box
          component={'span'}
          display={'flex'}
          alignItems={'center'}
          gap={0.5}
          pb={{xxs: 1.5, md: 0}}
          // pt={0.5}
        >
          <Typography variant="body3">Refer to these guidelines</Typography>
          <Tooltip
            title={`This is not a strict guideline. We only suggest this outline to
                make your script. Feel free to modify it as per you like.`}
            arrow
          >
            <Box lineHeight={0}>
              <Box component={IoIosInformationCircleOutline} fontSize={14} />
            </Box>
          </Tooltip>
        </Box>
      }
      sx={{
        position: 'fixed',
        width: {xxs: 'calc(100vw - 16px)', md: 280},
        right,
        bottom: {xxs: 100 - 16, md: 100},
      }}
    >
      <Box minWidth="100%">
        <AccordionGroup />
      </Box>
    </SidePopup>
  );
};

const AudioVideoView = dynamic(
  () => import('./internalComponents/audioVideo'),
  {
    ssr: false,
  }
);
interface ApplyStepsInterface {
  label: string;
  labelXs?: string;
  id: 'apply' | 'submit-a-form' | 'audio-video' | 'review-submit';
  customStyles?: any;
  component?: any;
  hideFooter?: boolean;
}

const StepperBox = styled(Box)<any>(({theme}: any) => ({
  gap: theme.spacing(1),
  transition: '0.3s ease',
  display: 'flex',
  alignItems: 'center',
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    textAlign: 'center',
    maxWidth: 'fit-content',
  },
}));

const StepperContainer = styled(Box)<any>(({theme}: any) => ({
  [theme.breakpoints.down('md')]: {
    '.wizard-step:last-child': {
      maxWidth: '55px',
    },
  },
}));

const StepIcon = (props: any) => {
  if (props.isActive) {
    return (
      <Box
        component={Image}
        src={isAppHirist ? OrangeCheckCircle : SurveyCheckGreen}
        width={{xxs: 20, md: 26}}
        height={{xxs: 20, md: 26}}
        alt="gradientCheckIcon"
      />
    );
  } else {
    return (
      <Box
        sx={{
          lineHeight: 0,
          width: {xxs: 20, md: 26},
          height: {xxs: 20, md: 26},
          border: '1px solid #333',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          borderRadius: '100%',
        }}
      >
        <Typography fontWeight={600} fontSize={10}>
          {props.label || '01'}
        </Typography>
      </Box>
    );
  }
};
const ApplyFlowComponent = (props: any) => {
  const ApplySteps: ApplyStepsInterface[] = [
    {
      id: 'apply',
      label: 'Apply',
      // component: () => <></>,
    },
    {
      id: 'submit-a-form',
      label: 'Submit a Form',
      component: ScreeningView,
      hideFooter: true,
    },
    {
      id: 'audio-video',
      label: 'Add Audio/Video Profile',
      labelXs: 'Audio/Video Profile',
      component: AudioVideoView,
    },
    {
      id: 'review-submit',
      label: 'Review & Submit',
      customStyles: {pr: 0},
      component: ReviewView,
    },
  ];
  const theme = useWrappedTheme();
  const [activeStep, setActiveStep] = useState(0);
  const [initialStep, setInitialStep] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setSaving] = useState(false);
  const [progressWidth, setProgressWidth] = useState('');
  const [isMinimalist, setMinimalistView] = useState(false);
  const [title, setTitle] = useState('');
  const [switchBlocked, setBlockSwitch] = useState(false);
  const [isNextDisabled, setIsNextDisabled] = useState(false);
  const [AudioVideo, setAudioVideo] = useState(false);
  const isLarge = useIsLargeDevice();
  const MAX_CONTAINER_WIDTH = isLarge ? 750 : '100%';

  const detail = useSelector(
    (state: AppState) => state.jobDetail,
    shallowEqual
  );
  const userState = useSelector((state: AppState) => state.user, shallowEqual);
  const location = detail.location ?? detail.locations;
  const router = useRouter();
  const screenViewTrackedRef = useRef<{[key: string]: boolean}>({});
  const isResumeOlderThan12Months = useMemo(() => {
    const resumeDate = parseResumeUpdatedAt(
      userState?.profile?.resumeUpdatedAt
    );
    if (!resumeDate) return false;

    const cutoffDate = new Date();
    cutoffDate.setMonth(cutoffDate.getMonth() - 12);
    return resumeDate < cutoffDate;
  }, [userState.profile.resumeUpdatedAt]);

  const handleStepProgress = () => {
    let firstElement: any = document.querySelector(
      '.applyFlowWizard .step-active'
    );

    let lastElement: any = document.querySelector(
      '.applyFlowWizard .step-in-active'
    );
    firstElement = firstElement?.getBoundingClientRect();
    lastElement = lastElement?.getBoundingClientRect();
    let width = '100%';
    if (!firstElement) {
      width = '0px';
    }
    if (lastElement && firstElement) {
      width = `${lastElement.left - firstElement?.left + 13}px`;
    }
    setProgressWidth(width);
    activateStep(activeStep);
  };
  useEffect(() => {
    if (activeStep) {
      handleStepProgress();
    }
    if (activeStep === 2) {
      setAudioVideo(ApplySteps[activeStep]?.component);
    }
  }, [activeStep]);

  useEffect(() => {
    const handleWindowResize = () => handleStepProgress();
    window.addEventListener('resize', handleWindowResize);
    return () => window.removeEventListener('resize', handleWindowResize);
  }, []);

  const activateStep = (step: number) => {
    const activeStep = ApplySteps[step];
    const isRoundZeroProfileExists =
      userState.profile.audioPath || userState.profile.videoPath;

    if (
      activeStep?.id === 'audio-video' &&
      detail.mediaResume > 0 &&
      !isRoundZeroProfileExists
    ) {
      setIsNextDisabled(true);
    }
  };
  const onNext = () => {
    switchToNextStep();
  };
  const isAudioVideoNextLoading =
    ApplySteps[activeStep]?.id === 'audio-video' && props.isApplyLoading;

  const onValidation = (value = false) => {
    setIsNextDisabled(value);
  };
  const hideFooter = useMemo(() => {
    return ApplySteps[activeStep]?.hideFooter;
  }, [activeStep]);

  const PageComponent = useMemo(() => {
    return ApplySteps[activeStep]?.component;
  }, [activeStep]);

  const setFullscreenMode = (headerTitle: string) => {
    if (headerTitle) {
      setMinimalistView(true);
      setTitle(headerTitle);
    } else {
      setMinimalistView(false);
    }
  };

  const routeBackToJD = (query?: any) => {
    const slug = kebabCase(detail?.title?.replace(/&/g, '-and-'));
    // if (isMinimalist) {
    //   setTitle('');
    //   setMinimalistView(false);
    // } else {
    //   let path = `/j/${slug}-${detail?.id}`;
    //   const params = new URLSearchParams(query);
    //   path = query ? path + `?${params?.toString()}` : path;
    //   // router.push(path);
    //   window.location.href = path;
    // }
    let path = `/j/${slug}-${detail?.id}`;
    const params = new URLSearchParams(query);
    path = query ? path + `?${params?.toString()}` : path;
    // router.push(path);
    window.location.href = path;
  };

  const handleBackClick: any = (e: MouseEvent | undefined, query?: any) => {
    if (isMinimalist) {
      setTitle('');
      setMinimalistView(false);
    } else {
      switchToPrevStep(query);
    }
  };
  const viewableSteps = useMemo(() => {
    return ApplySteps.filter((step: ApplyStepsInterface) => {
      if (step?.id === 'submit-a-form' && detail.assessmentFlags === 0) {
        return false;
      }
      const isRoundZeroProfileExists =
        userState.profile.audioPath || userState.profile.videoPath;
      const isCurrentAudioVideoStep =
        ApplySteps[activeStep]?.id === 'audio-video';
      if (
        step?.id === 'audio-video' &&
        (detail.mediaResume === 0 ||
          (isRoundZeroProfileExists && !isCurrentAudioVideoStep))
      ) {
        return false;
      }
      if (step?.id === 'review-submit') {
        if (isAppIIMJobs) {
          return isResumeOlderThan12Months;
        }
        return false;
      }
      return true;
    });
  }, [
    detail,
    activeStep,
    userState.profile.email,
    userState.profile.audioPath,
    userState.profile.videoPath,
    isResumeOlderThan12Months,
  ]);
  const handleSave = async () => {
    setSaving(true);
    await jobDetailService.saveJob(detail.id);
    if (props.handleSave) {
      props.handleSave();
    }
    routeBackToJD({action: 'saved'});
  };
  useEffect(() => {
    const currentStepId = ApplySteps[activeStep]?.id;
    const isCurrentStepViewable = !!currentStepId
      ? viewableSteps.some(step => step.id === currentStepId)
      : false;

    if (isCurrentStepViewable && activeStep > 0) {
      setTimeout(() => {
        handleStepProgress();
      }, 10);
      return;
    }

    if (viewableSteps.length && viewableSteps.length > 1) {
      const landingStep = viewableSteps.find(step => {
        if (step.id === 'submit-a-form' && detail.assessmentCompleted === 0) {
          return true;
        } else if (
          step.id === 'audio-video' &&
          !(userState.profile.audioPath || userState.profile.videoPath)
        ) {
          return true;
        }
        return false;
      });
      if (landingStep) {
        const stepIndex = getStepIndex(landingStep);
        setActiveStep(stepIndex);
        setInitialStep(stepIndex);
      } else {
        setActiveStep(1);
        setInitialStep(1);
      }
    } else {
      setActiveStep(1);
      setInitialStep(1);
    }
    setTimeout(() => {
      handleStepProgress();
    }, 10);
  }, [viewableSteps, activeStep, detail.assessmentCompleted]);

  useEffect(() => {
    if (isLoading) return;
    const activeStepId = ApplySteps[activeStep]?.id;
    if (!['submit-a-form', 'review-submit'].includes(activeStepId)) return;

    const trackKey = `${detail?.id}-${activeStepId}`;
    if (screenViewTrackedRef.current[trackKey]) return;
    screenViewTrackedRef.current[trackKey] = true;

    const getQueryString = (value: string | string[] | undefined) =>
      Array.isArray(value) ? (value[0] ?? '') : (value ?? '');

    TrackEventUBA('reviewSubmitView', 'view', {
      sessionUsername: sessionStorage.getItem('userId') || '',
      ref: getQueryString(router.query.ref),
      jobID: detail?.id || '',
      jobPos: getQueryString(router.query.jobPos),
      screenType: activeStepId === 'review-submit' ? 'Review' : 'Screening',
    });
  }, [
    activeStep,
    isLoading,
    router.query.ref,
    router.query.jobPos,
    detail?.id,
  ]);

  const switchToNextStep = () => {
    const activeStepIndex = viewableSteps
      .map(s => s.id)
      .indexOf(ApplySteps[activeStep].id);
    const nextStep = viewableSteps[activeStepIndex + 1];
    if (!nextStep) {
      handleApplyJob();
      return;
    }
    const nextStepIndex = ApplySteps.map(s => s.id).indexOf(nextStep.id);
    setActiveStep(nextStepIndex);
    setBlockSwitch(false);
  };

  const switchToPrevStep = (query?: any) => {
    const activeStepIndex = viewableSteps
      .map(s => s.id)
      .indexOf(ApplySteps[activeStep].id);
    const prevStep = viewableSteps[activeStepIndex - 1];
    const prevStepIndex = ApplySteps.map(s => s.id).indexOf(prevStep.id);
    console.log(prevStepIndex, activeStepIndex, prevStep, 'switchToPrevStep');
    if (prevStepIndex > 0) {
      setActiveStep(prevStepIndex);
      setBlockSwitch(false);
    } else {
      routeBackToJD(query);
    }
  };

  const handleApplyJob = () => {
    if (props.isApplyLoading) return;
    props.onApply?.();
    // setIsNextDisabled(true);
    // setIsApplying(true);
    // setTimeout(() => {
    //   setIsNextDisabled(false);
    //   setIsApplying(false);
    // }, 1000);
  };
  useEffect(() => {
    if (userState.profile.email) {
      setIsLoading(false);
    }
  }, [userState.profile.email]);

  const deleteRecordedData = async (mediaType: string) => {
    return await mediaService.deleteRoundZeroAudioVideo(mediaType);
  };

  const getStepIndex = (step: ApplyStepsInterface) => {
    const index = ApplySteps.map(s => s.id).indexOf(step.id);
    return index;
  };
  const onStepClick = (step: ApplyStepsInterface, stepIndex: number) => {
    if (switchBlocked) {
      if (typeof switchBlocked === 'string') {
        showErrorToast(switchBlocked);
      }
      return;
    }
    if (stepIndex <= initialStep) {
      setActiveStep(getStepIndex(step));
    } else {
      const currentStep = ApplySteps[activeStep];
      if (
        currentStep.id === 'audio-video' &&
        (userState.profile.videoPath || userState.profile.audioPath)
      ) {
        setActiveStep(getStepIndex(step));
      } else if (
        currentStep.id === 'submit-a-form' &&
        detail.assessmentCompleted
      ) {
        setActiveStep(getStepIndex(step));
      } else {
        if (currentStep.id === 'audio-video' && detail.mediaResume == 2) {
          handleSkip();
        } else {
          showErrorToast('Please complete mandatory steps to proceed');
        }
      }
    }
  };
  const [skipAlertProps, setSkipAlertProps] = useState<any>({});

  const [SkipAlert, toggleSkipAlert] = useAlertCallback({
    primaryCallback: console.log,
    secondaryCallback: () => {
      switchToNextStep();
      sendSwitchEvent({});
    },
    ...skipAlertProps,
  });

  const handleSkip = () => {
    if (switchBlocked) {
      showErrorToast("Can't Skip while recording");
      return;
    }
    if (
      ApplySteps[activeStep].id === 'audio-video' &&
      (userState.profile.videoPath || userState.profile.audioPath)
    ) {
      setSkipAlertProps((s: any) => ({
        ...s,
        title: 'Your Audio/Video profile is already uploaded',
        subtitle: `Please delete your profile to skip this step or continue applying with your profile.`,
        primaryAction: 'Continue',
        primaryCallback: () => {
          switchToNextStep();
        },
        secondaryAction: 'Delete & Skip',
        secondaryCallback: () => {
          switchToNextStep();
          sendSwitchEvent({delete: true});
          // deleteRecordedData('RoundZeroVideo')
          //   .then(console.log)
          //   .catch(console.error);
          // deleteRecordedData('RoundZeroAudio')
          //   .then(console.log)
          //   .catch(console.error);
        },
      }));
    } else {
      setSkipAlertProps({
        primaryAction: 'Add Profile',
        secondaryAction: 'Skip Anyway',
        subtitle: (
          <>
            This gives you a{' '}
            <Typography component={'span'} color={'primary'}>
              70%
            </Typography>{' '}
            higher chance of getting noticed by the recruiter. We strongly
            encourage you to make one!
          </>
        ),
        title: 'We highlight candidates with a Video/Audio Resume.',
      });
    }
    toggleSkipAlert();
    if (props.handleSkip) {
      props.handleSkip();
    }
  };

  const toggleBlockSwitching = (error: any) => {
    console.log(error);
    setBlockSwitch(error);
  };

  return (
    <>
      {isLoading ? (
        <Loader />
      ) : (
        <>
          <Box
            className="applyFlowContainer"
            pb={hideFooter || isMinimalist ? 2 : !isLarge ? 22 : 14}
          >
            <Box pb={{xxs: 1, md: 0}}>
              <Box
                sx={{
                  mb: isMinimalist || !isLarge ? 0 : 2,
                  display: 'flex',
                  gap: 1.5,
                }}
              >
                <Image
                  width={18}
                  height={18}
                  src={LeftArrow}
                  alt={''}
                  onClick={handleBackClick}
                  style={{cursor: 'pointer', marginTop: 1.5}}
                />
                <Box>
                  <Typography
                    variant="subtitle1"
                    fontSize={{xxs: 14, md: 16}}
                    mb={1}
                  >
                    You are Applying to: {title || detail.title}
                  </Typography>
                  {isLarge && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        columnGap: 1.5,
                        color: 'text.secondary',
                        flexWrap: 'wrap',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          width: 'fit-content',
                          gap: 0.4,
                          flexWrap: 'wrap',
                        }}
                      >
                        <Typography component={'span'} variant="subtitle2">
                          {`${detail.min}-${detail.max} Yrs`}
                        </Typography>
                        <Typography
                          lineHeight={0}
                          component={'span'}
                          variant="body2"
                        >
                          <Image
                            src={Dot}
                            width={4}
                            height={4}
                            alt="."
                            style={{margin: '0 8px'}}
                          />
                        </Typography>
                        <Typography component={'span'} variant="subtitle2">
                          {location?.length > 3
                            ? 'Multiple Locations'
                            : location?.map(l => l.name).join(', ')}
                        </Typography>
                      </Box>
                      <Typography
                        component={'p'}
                        color="text.secondary"
                        variant="body2"
                      >
                        <Typography
                          component={'span'}
                          color="secondary.main"
                          variant="subtitle2"
                        >
                          Posted by:&nbsp;&nbsp;
                          {detail.confidential ? (
                            <Typography
                              component={'span'}
                              color={'primary.main'}
                              variant="subtitle2"
                            >
                              {detail?.recruiter?.recruiterName}
                            </Typography>
                          ) : (
                            <Link
                              sx={{textDecoration: 'none'}}
                              target="__blank"
                              href={getOldLink(
                                `/r/${
                                  detail?.recruiter?.recruiterId
                                }-${kebabCase(
                                  detail?.recruiter?.recruiterName.replace(
                                    /&/g,
                                    '-and-'
                                  )
                                )}`
                              )}
                            >
                              {detail?.recruiter?.recruiterName},
                            </Link>
                          )}
                        </Typography>{' '}
                        {detail.confidential
                          ? ''
                          : detail?.recruiter?.designation}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>

              {/* MAIN STEPS COMPONENT */}
              {!isMinimalist && (
                <Box
                  width={'100%'}
                  sx={{backgroundColor: 'border.main'}}
                  display={'flex'}
                  justifyContent={'center'}
                  borderRadius={6}
                >
                  <Box
                    p={2}
                    sx={{maxWidth: MAX_CONTAINER_WIDTH, width: '100%'}}
                  >
                    <StepperContainer
                      justifyContent={'space-between'}
                      display={'flex'}
                      flexWrap={'wrap'}
                      className="applyFlowWizard"
                      component={isLarge ? undefined : Grid}
                      container
                      columnGap={1}
                      sx={{
                        counterReset: 'index',
                      }}
                    >
                      {viewableSteps.map((step, index) => {
                        const stepIndex = getStepIndex(step);
                        return (
                          <StepperBox
                            xxs={
                              step?.id === 'audio-video'
                                ? viewableSteps.length
                                : true
                            }
                            item
                            key={`apply-flow-step-${step?.id}-${stepIndex}`}
                            component={!isLarge ? Grid : undefined}
                            className={`${
                              stepIndex < activeStep
                                ? 'step-active'
                                : 'step-in-active'
                            } wizard-step`}
                            onClick={
                              step.id === 'apply'
                                ? undefined
                                : () => onStepClick(step, stepIndex)
                            }
                            sx={{
                              ...(step.customStyles || {}),
                              opacity: stepIndex > activeStep ? 0.5 : 1,
                              cursor:
                                step.id === 'apply'
                                  ? 'unset'
                                  : stepIndex == activeStep
                                    ? 'unset'
                                    : 'pointer',
                            }}
                          >
                            <StepIcon
                              isActive={stepIndex < activeStep}
                              label={`0${index + 1}`}
                            />
                            <Typography
                              fontWeight={500}
                              fontSize={{xxs: 10, xs: 12, md: 14}}
                            >
                              {(isLarge ? step.label : step.labelXs) ||
                                step.label}
                            </Typography>
                          </StepperBox>
                        );
                      })}
                    </StepperContainer>
                    <Box minWidth={MAX_CONTAINER_WIDTH} mt={{xxs: 1, md: 2.5}}>
                      <Box
                        width={'100%'}
                        sx={{
                          backgroundColor: '#fff',
                          height: 8,
                          borderRadius: 10,
                          overflow: 'hidden',
                        }}
                      >
                        <Box
                          width={progressWidth}
                          sx={{
                            background: isAppHirist
                              ? theme.palette.primary.main
                              : 'linear-gradient(90deg, #225448 0%, #7E9F67 100%)',
                            height: 8,
                            transition: '0.2s ease',
                            borderRadius: 10,
                          }}
                        ></Box>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>
            <Box
              display={'flex'}
              justifyContent={'center'}
              sx={{
                scrollbarWidth: 'unset !important',
              }}
            >
              <Box
                mt={isMinimalist ? 0 : {xxs: 1, md: 3}}
                width={'100%'}
                display={'flex'}
                justifyContent={'center'}
              >
                {/* {viewableSteps.slice(0, -1).map(step => {
                  if (!step.component) return null;
                  const Comp = step.component;
                  const stepIndex = getStepIndex(step);
                  return (
                    <Comp
                      key={`page-component-${step.id}`}
                      isMinimalist={isMinimalist}
                      setMinimalistView={setFullscreenMode}
                      onNext={switchToNextStep}
                      onValidation={onValidation}
                      onBlockSwitch={toggleBlockSwitching}
                      containerStyles={{
                        maxWidth: MAX_CONTAINER_WIDTH,
                        width: '100%',
                        display: activeStep == stepIndex ? 'unset' : 'none',
                      }}
                      {...props}
                    />
                  );
                })}
                {activeStep == 3 && (
                  <PageComponent
                    isMinimalist={isMinimalist}
                    setMinimalistView={setFullscreenMode}
                    onNext={switchToNextStep}
                    onValidation={onValidation}
                    onBlockSwitch={toggleBlockSwitching}
                    containerStyles={{
                      maxWidth: MAX_CONTAINER_WIDTH,
                      width: '100%',
                    }}
                    {...props}
                  />
                )} */}
                {activeStep !== 2 && (
                  <PageComponent
                    isMinimalist={isMinimalist}
                    setMinimalistView={setFullscreenMode}
                    onNext={switchToNextStep}
                    onValidation={onValidation}
                    onBlockSwitch={toggleBlockSwitching}
                    handleApplyJob={handleApplyJob}
                    containerStyles={{
                      maxWidth: MAX_CONTAINER_WIDTH,
                      width: '100%',
                    }}
                    {...props}
                  />
                )}
                {AudioVideo && (
                  <AudioVideoView
                    isMinimalist={isMinimalist}
                    setMinimalistView={setFullscreenMode}
                    onNext={switchToNextStep}
                    onValidation={onValidation}
                    onBlockSwitch={toggleBlockSwitching}
                    containerStyles={{
                      maxWidth: MAX_CONTAINER_WIDTH,
                      width: '100%',
                      display: activeStep == 2 ? 'unset' : 'none',
                    }}
                    {...props}
                  />
                )}
              </Box>
            </Box>
            {!hideFooter && !isMinimalist && (
              <Box
                sx={{
                  background: '#fff',
                  position: 'fixed',
                  maxWidth: {xxs: 'calc(100vw - 16px)', md: 1252},
                  right: 0,
                  left: 0,
                  margin: 'auto',
                  bottom: 0,
                  height: '100px',
                  borderRadius: '16px 16px 0 0',
                  boxShadow: '0px 4px 4px 0px #0000000F inset',
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: {
                    xxs:
                      ApplySteps[activeStep]?.id === 'review-submit'
                        ? 'center'
                        : 'space-between',
                    md: 'end',
                  },
                  px: {xxs: 3, md: 5},
                  zIndex: 1000,
                }}
              >
                {ApplySteps[activeStep]?.id === 'audio-video' &&
                detail.mediaResume == 2 ? (
                  <Button sx={{px: {xxs: 0, md: 3}}} onClick={handleSkip}>
                    Skip this step
                  </Button>
                ) : ApplySteps[activeStep]?.id !== 'review-submit' ? (
                  <Button
                    disabled={isSaving}
                    sx={{px: {xxs: 0, md: 3}}}
                    onClick={handleSave}
                  >
                    {!isSaving ? (
                      <Typography fontSize={{xxs: 16}} fontWeight={500}>
                        Save & Apply Later
                      </Typography>
                    ) : (
                      <Box sx={{minWidth: 150}}>
                        <CircleLoader sx={{color: 'primary'}} size={27} />
                      </Box>
                    )}
                  </Button>
                ) : null}
                {ApplySteps[activeStep]?.id === 'review-submit' ? (
                  <Button
                    variant="contained"
                    color="secondary"
                    sx={{
                      minWidth: 210,
                      ...(process.env.NEXT_PUBLIC_APP_NAME === 'hirist' && {
                        bgcolor: `${theme.palette.primary.main} !important`,
                        border: 'none',
                        borderRadius: '8px',
                        color: 'white !important',
                        ':disabled': {
                          bgcolor: theme.palette.primary.main,
                        },
                      }),
                    }}
                    disabled={props.isApplyLoading}
                    onClick={handleApplyJob}
                  >
                    {!props.isApplyLoading ? (
                      <Typography fontSize={{xxs: 16, md: 18}} fontWeight={500}>
                        Review & Submit
                      </Typography>
                    ) : (
                      <CircleLoader sx={{color: '#fff'}} size={27} />
                    )}
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    color="secondary"
                    sx={{minWidth: {xxs: 150, md: 210}}}
                    disabled={isNextDisabled || isAudioVideoNextLoading}
                    onClick={onNext}
                    tooltipTitle="Record your resume to continue to the next step"
                  >
                    {isAudioVideoNextLoading ? (
                      <CircleLoader sx={{color: '#fff'}} size={27} />
                    ) : (
                      <Typography fontSize={{xxs: 16, md: 18}} fontWeight={500}>
                        Next
                      </Typography>
                    )}
                  </Button>
                )}
              </Box>
            )}
            {activeStep === 2 && !isMinimalist && <InfoPopup />}
          </Box>
          <SkipAlert />
        </>
      )}
    </>
  );
};

export default ApplyFlowComponent;
